import React, { useState, useEffect, useCallback } from 'react';
import PageContainer from '../components/containers/PageContainer';
import {
    Card,
    CardHeader,
    CardContent,
    IconButton,
    TableCell,
    TableRow,
    Tooltip,
    Select,
    MenuItem,
    Box,
    Button,
    Tabs,
    Tab,
    Typography,
    Chip,
} from '@mui/material';
import api from '../config/axios';
import { toast } from 'react-toastify';
import Field from '../components/ui/Field';
import CustomTable from '../components/ui/CustomTable';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ModalContainer from '../components/containers/ModalContainer';
import StatusComp from '../components/common/StatusComp';
import dayjs from 'dayjs';
import DateFilter from '../components/common/DateFilter';
import { useSearchParams } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

const PendingOrders = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    // Tab state
    const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'single');

    // Status state
    const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'pending');

    // States for orders data
    const [orders, setOrders] = useState([]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loader, setLoader] = useState(false);
    const [order, setOrder] = useState({});
    const [showOrderDetails, setShowOrderDetails] = useState(false);

    // States for filters
    const [sort, setSort] = useState(searchParams.get('sort') || 'desc');
    const [page, setPage] = useState(parseInt(searchParams.get('page') || '1', 10));
    const [limit, setLimit] = useState(parseInt(searchParams.get('limit') || '10', 10));
    const [search, setSearch] = useState(searchParams.get('search') || '');
    const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
    const [dateRange, setDateRange] = useState({
        startDate: searchParams.get('from') ? dayjs(searchParams.get('from')).toDate() : null,
        endDate: searchParams.get('to') ? dayjs(searchParams.get('to')).toDate() : null,
        key: 'selection',
    });

    // Modal functions
    const onCloseOrderDetailsModal = () => setShowOrderDetails(false);
    const onOpenOrderDetailsModal = () => setShowOrderDetails(true);

    // Update URL with current filter state
    const updateUrl = useCallback(() => {
        const params = new URLSearchParams();

        params.set('tab', activeTab);
        params.set('status', statusFilter);
        if (search) params.set('search', search);
        if (sort) params.set('sort', sort);
        if (page > 1) params.set('page', page.toString());
        if (limit !== 10) params.set('limit', limit.toString());
        if (dateRange.startDate) params.set('from', dayjs(dateRange.startDate).format('YYYY-MM-DD'));
        if (dateRange.endDate) params.set('to', dayjs(dateRange.endDate).format('YYYY-MM-DD'));

        setSearchParams(params);
    }, [activeTab, statusFilter, search, sort, page, limit, dateRange, setSearchParams]);

    // Fetch orders from API
    const getOrders = useCallback(async () => {
        setLoader(true);

        try {
            const params = new URLSearchParams();
            params.append('page', page.toString());
            params.append('limit', limit.toString());
            params.append('sort', sort);
            params.append('status', statusFilter);

            if (search) params.append('search', search);
            if (dateRange.startDate) params.append('from', dayjs(dateRange.startDate).format('YYYY-MM-DD'));
            if (dateRange.endDate) params.append('to', dayjs(dateRange.endDate).format('YYYY-MM-DD'));

            const endpoint =
                activeTab === 'single'
                    ? `/admin/pending-orders/single?${params.toString()}`
                    : `/admin/pending-orders/bulk?${params.toString()}`;

            const res = await api.get(endpoint);

            setOrders(res.data.orders || res.data.bulkOrders || []);
            setTotalPages(res.data.totalPages);
            setTotal(res.data.total);
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || 'Failed to fetch pending orders');
        } finally {
            setLoader(false);
        }
    }, [page, limit, sort, search, dateRange, activeTab, statusFilter]);

    // Handle search button click
    const handleSearch = () => {
        setSearch(searchInput);
        setPage(1); // Reset to first page on new search
    };

    // Handle tab change
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        setStatusFilter('pending'); // Reset to pending status when switching tabs
        setPage(1); // Reset to first page when switching tabs
        setOrders([]); // Clear orders while switching
    };

    // Handle status change
    const handleStatusChange = (newStatus) => {
        setStatusFilter(newStatus);
        setPage(1); // Reset to first page when changing status
    };

    // Render items helper function
    const renderItems = (order) => {
        if (order.Items && Array.isArray(order.Items) && order.Items.length > 0) {
            // New orders with Items array
            return (
                <Box>
                    {order.Items.map((item, index) => (
                        <Typography key={item.LineItemID || index} variant="body2" sx={{ mb: 0.5 }}>
                            {item.Quantity} x {item.Title}
                        </Typography>
                    ))}
                </Box>
            );
        } else if (order.itemDetails) {
            // Old orders with itemDetails string
            const items = order.itemDetails.split(' - ');
            return (
                <Box>
                    {items.map((item, index) => (
                        <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                            {item.trim()}
                        </Typography>
                    ))}
                </Box>
            );
        }
        return <Typography variant="body2">No items</Typography>;
    };

    // Check if any filter is active
    const hasActiveFilters = () => {
        return (
            search !== '' ||
            statusFilter !== 'pending' ||
            sort !== 'desc' ||
            limit !== 10 ||
            dateRange.startDate !== null ||
            dateRange.endDate !== null ||
            page > 1
        );
    };

    // Clear all filters
    const handleClearFilters = () => {
        setSearch('');
        setSearchInput('');
        setSort('desc');
        setLimit(10);
        setStatusFilter('pending');
        setPage(1);
        setDateRange({
            startDate: null,
            endDate: null,
            key: 'selection',
        });
    };

    // Sync URL params with state
    useEffect(() => {
        updateUrl();
    }, [activeTab, statusFilter, sort, page, limit, search, dateRange, updateUrl]);

    // Fetch orders when URL params change
    useEffect(() => {
        getOrders();
    }, [activeTab, searchParams, getOrders]);

    // Render Single Orders Table
    const renderSingleOrdersTable = () => (
        <CustomTable
            sx={{ mt: 2 }}
            fields={[
                'ORDER ID',
                'USER',
                'FROM NAME',
                'TO NAME',
                'ITEMS',
                'TOTAL QTY',
                'LABEL TYPE',
                'WEIGHT',
                'STATUS',
                'CREATED AT',
                'ACTION',
            ]}
            dense={false}
            loading={loader}
            pagination
            count={totalPages}
            page={page}
            setPage={setPage}>
            {orders.map((order) => (
                <TableRow key={order._id}>
                    <TableCell>{order._id}</TableCell>
                    <TableCell>{order.user?.username || order.user?.email || 'N/A'}</TableCell>
                    <TableCell>{order.FromName}</TableCell>
                    <TableCell>{order.ToName}</TableCell>
                    <TableCell sx={{ maxWidth: '300px' }}>{renderItems(order)}</TableCell>
                    <TableCell>
                        <Typography variant="body2" fontWeight="600" color="primary.main">
                            {order.TotalQty || 0}
                        </Typography>
                    </TableCell>
                    <TableCell>{order.labelType?.name || 'N/A'}</TableCell>
                    <TableCell>
                        {order.Weight}
                        {order.labelType?.name?.includes('First Class') ? ' oz' : ' lb'}
                    </TableCell>
                    <TableCell>
                        <StatusComp status={order.status} />
                    </TableCell>
                    <TableCell>{new Date(order.createdAt).toLocaleString()}</TableCell>
                    <TableCell>
                        <Tooltip title="View Order Details">
                            <IconButton
                                onClick={() => {
                                    setOrder(order);
                                    onOpenOrderDetailsModal();
                                }}>
                                <VisibilityOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </TableCell>
                </TableRow>
            ))}
        </CustomTable>
    );

    // Render Bulk Orders Table
    const renderBulkOrdersTable = () => (
        <CustomTable
            sx={{ mt: 2 }}
            fields={[
                'BULK ORDER ID',
                'USER',
                'TOTAL ORDERS',
                'PROCESSED',
                'FAILED',
                'STATUS',
                'FILE NAME',
                'CREATED AT',
                'ACTION',
            ]}
            dense={false}
            loading={loader}
            pagination
            count={totalPages}
            page={page}
            setPage={setPage}>
            {orders.map((bulkOrder) => (
                <TableRow key={bulkOrder._id}>
                    <TableCell>{bulkOrder._id}</TableCell>
                    <TableCell>{bulkOrder.user?.username || bulkOrder.user?.email || 'N/A'}</TableCell>
                    <TableCell>{bulkOrder.orderCount || 0}</TableCell>
                    <TableCell sx={{ color: 'success.main' }}>{bulkOrder.processedOrders?.length || 0}</TableCell>
                    <TableCell sx={{ color: 'error.main' }}>{bulkOrder.failedOrders?.length || 0}</TableCell>
                    <TableCell>
                        <StatusComp status={bulkOrder.status} />
                    </TableCell>
                    <TableCell>{bulkOrder.fileName || 'N/A'}</TableCell>
                    <TableCell>{new Date(bulkOrder.createdAt).toLocaleString()}</TableCell>
                    <TableCell>
                        <Tooltip title="View Bulk Order Details">
                            <IconButton
                                onClick={() => {
                                    setOrder(bulkOrder);
                                    onOpenOrderDetailsModal();
                                }}>
                                <VisibilityOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </TableCell>
                </TableRow>
            ))}
        </CustomTable>
    );

    return (
        <PageContainer title="Veeqo Orders" desc="Manage all veeqo single and bulk orders">
            {/* Tabs Section */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    aria-label="pending orders tabs"
                    sx={{
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontSize: '1rem',
                            fontWeight: 500,
                            minWidth: 150,
                        },
                    }}>
                    <Tab label="Single Pending Orders" value="single" />
                    <Tab label="Bulk Pending Orders" value="bulk" />
                </Tabs>
            </Box>

            {/* Stats Section */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" color="text.secondary">
                    Total {activeTab === 'single' ? 'Single' : 'Bulk'} Orders ({statusFilter}):{' '}
                    <Typography component="span" variant="h6" color="primary.main" fontWeight="bold">
                        {total.toLocaleString()}
                    </Typography>
                </Typography>
            </Box>

            {/* Filters Section */}
            <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Search Bar */}
                <Box sx={{ display: 'flex', direction: 'row', gap: 1, alignItems: 'center' }}>
                    <Field
                        placeholder={
                            activeTab === 'single'
                                ? 'Search orders using order id, name, email address'
                                : 'Search bulk orders using bulk order id, user email'
                        }
                        value={searchInput}
                        onChange={(e) => {
                            setSearchInput(e.target.value);
                        }}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && searchInput.length >= 3) {
                                handleSearch();
                            }
                        }}
                        sx={{ flex: 1, minWidth: '250px' }}
                    />
                    <Button
                        variant="contained"
                        size="large"
                        disabled={searchInput.length < 3}
                        onClick={handleSearch}
                        startIcon={<SearchIcon />}
                        sx={{ minWidth: '100px' }}>
                        Search
                    </Button>
                </Box>

                {/* Filter Controls */}
                <Box sx={{ display: 'flex', direction: 'row', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    {/* Status Tabs */}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {(activeTab === 'single'
                            ? ['pending', 'processing', 'partial', 'completed', 'failed']
                            : ['pending', 'processing', 'completed', 'failed']
                        ).map((status) => (
                            <Chip
                                key={status}
                                label={status.charAt(0).toUpperCase() + status.slice(1)}
                                onClick={() => handleStatusChange(status)}
                                variant={statusFilter === status ? 'filled' : 'outlined'}
                                color={statusFilter === status ? 'primary' : 'default'}
                                sx={{
                                    cursor: 'pointer',
                                    textTransform: 'capitalize',
                                    fontWeight: statusFilter === status ? 'bold' : 'normal',
                                }}
                            />
                        ))}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <span style={{ fontSize: '14px', fontWeight: 500 }}>Sort:</span>
                        <Select
                            size="small"
                            value={sort}
                            onChange={(e) => {
                                setSort(e.target.value);
                            }}
                            sx={{ minWidth: '120px' }}>
                            <MenuItem value="asc">Ascending</MenuItem>
                            <MenuItem value="desc">Descending</MenuItem>
                        </Select>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <span style={{ fontSize: '14px', fontWeight: 500 }}>Limit:</span>
                        <Select
                            size="small"
                            value={limit}
                            onChange={(e) => {
                                setLimit(e.target.value);
                                setPage(1);
                            }}
                            sx={{ minWidth: '80px' }}>
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                        </Select>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <DateFilter dateRange={dateRange} setDateRange={setDateRange} apply={getOrders} />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {hasActiveFilters() && (
                            <Button
                                variant="outlined"
                                size="small"
                                color="error"
                                onClick={handleClearFilters}
                                startIcon={<ClearIcon />}
                                sx={{ minWidth: '120px' }}>
                                Clear Filters
                            </Button>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Table Section */}
            {activeTab === 'single' ? renderSingleOrdersTable() : renderBulkOrdersTable()}

            {/* Order Details Modal */}
            <ModalContainer
                open={showOrderDetails}
                onClose={onCloseOrderDetailsModal}
                hideActions
                title={activeTab === 'single' ? 'View Order Details' : 'View Bulk Order Details'}>
                {activeTab === 'single' ? (
                    <div className="row">
                        <div className="col-lg-6">
                            <Card>
                                <CardHeader title="From" />
                                <CardContent>
                                    <div className="card-text">
                                        <span className="fw-bold">Name:</span> {order.FromName} <br />
                                        <span className="fw-bold">Company:</span> {order.FromCompany} <br />
                                        <span className="fw-bold">Street:</span> {order.FromStreet} <br />
                                        <span className="fw-bold">Street2:</span> {order.FromStreet2} <br />
                                        <span className="fw-bold">City:</span> {order.FromCity} <br />
                                        <span className="fw-bold">State:</span> {order.FromState} <br />
                                        <span className="fw-bold">Zip:</span> {order.FromZip} <br />
                                        <span className="fw-bold">Country:</span> {order.FromCountry} <br />
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                        <div className="col-lg-6">
                            <Card>
                                <CardHeader title="To" />
                                <CardContent>
                                    <div className="card-text">
                                        <span className="fw-bold">Name:</span> {order.ToName} <br />
                                        <span className="fw-bold">Company:</span> {order.ToCompany} <br />
                                        <span className="fw-bold">Street:</span> {order.ToStreet} <br />
                                        <span className="fw-bold">Street2:</span> {order.ToStreet2} <br />
                                        <span className="fw-bold">City:</span> {order.ToCity} <br />
                                        <span className="fw-bold">State:</span> {order.ToState} <br />
                                        <span className="fw-bold">Zip:</span> {order.ToZip} <br />
                                        <span className="fw-bold">Country:</span> {order.ToCountry} <br />
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                        <div className="col-lg-12 mt-3">
                            <Card>
                                <CardHeader title="Items Details" />
                                <CardContent>
                                    <div className="card-text">
                                        <span className="fw-bold">Total Quantity:</span> {order.TotalQty || 0} <br />
                                        <br />
                                        <span className="fw-bold">Items:</span>
                                        <br />
                                        {order.Items && Array.isArray(order.Items) && order.Items.length > 0 ? (
                                            <Box sx={{ mt: 1, pl: 2 }}>
                                                {order.Items.map((item, index) => (
                                                    <Box key={item.LineItemID || index} sx={{ mb: 1 }}>
                                                        <Typography variant="body2">
                                                            • <span className="fw-bold">{item.Quantity}x</span>{' '}
                                                            {item.Title}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        ) : order.itemDetails ? (
                                            <Box sx={{ mt: 1, pl: 2 }}>
                                                {order.itemDetails.split(' - ').map((item, index) => (
                                                    <Typography key={index} variant="body2" sx={{ mb: 1 }}>
                                                        • {item.trim()}
                                                    </Typography>
                                                ))}
                                            </Box>
                                        ) : (
                                            <Typography variant="body2" sx={{ mt: 1, pl: 2, fontStyle: 'italic' }}>
                                                No items available
                                            </Typography>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                        <div className="col-lg-12 mt-3">
                            <Card>
                                <CardHeader title="Additional Details" />
                                <CardContent>
                                    <div className="card-text">
                                        <span className="fw-bold">Label Type:</span> {order.labelType?.name} <br />
                                        <span className="fw-bold">Weight:</span> {order.Weight}
                                        {order.labelType?.name?.includes('First Class') ? (
                                            <span className="fw-bold"> oz</span>
                                        ) : (
                                            <span className="fw-bold"> lb</span>
                                        )}{' '}
                                        <br />
                                        <span className="fw-bold">Status:</span> {order.status} <br />
                                        <span className="fw-bold">Created At:</span>{' '}
                                        {new Date(order.createdAt).toLocaleString()} <br />
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                ) : (
                    <div className="row">
                        <div className="col-lg-12">
                            <Card>
                                <CardHeader title="Bulk Order Details" />
                                <CardContent>
                                    <div className="card-text">
                                        <span className="fw-bold">Bulk Order ID:</span> {order._id} <br />
                                        <span className="fw-bold">User:</span>{' '}
                                        {order.user?.username || order.user?.email} <br />
                                        <span className="fw-bold">Total Orders:</span> {order.orderCount} <br />
                                        <span className="fw-bold">Processed Orders:</span>{' '}
                                        {order.processedOrders?.length || 0} <br />
                                        <span className="fw-bold">Failed Orders:</span>{' '}
                                        {order.failedOrders?.length || 0} <br />
                                        <span className="fw-bold">Status:</span> {order.status} <br />
                                        <span className="fw-bold">Created At:</span>{' '}
                                        {new Date(order.createdAt).toLocaleString()} <br />
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                        {order.orders && Array.isArray(order.orders) && order.orders.length > 0 && (
                            <div className="col-lg-12 mt-3">
                                <Card>
                                    <CardHeader title="Individual Orders in Bulk" />
                                    <CardContent>
                                        <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                                            {order.orders.map((individualOrder, index) => (
                                                <Box
                                                    key={individualOrder._id || index}
                                                    sx={{
                                                        mb: 2,
                                                        p: 2,
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        borderRadius: 1,
                                                    }}>
                                                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                                        Order #{index + 1}
                                                    </Typography>
                                                    <div className="card-text">
                                                        <span className="fw-bold">From:</span>{' '}
                                                        {individualOrder.FromName} <br />
                                                        <span className="fw-bold">To:</span> {individualOrder.ToName}{' '}
                                                        <br />
                                                        <span className="fw-bold">Total Quantity:</span>{' '}
                                                        {individualOrder.TotalQty || 0} <br />
                                                        <br />
                                                        <span className="fw-bold">Items:</span>
                                                        <br />
                                                        {individualOrder.Items &&
                                                        Array.isArray(individualOrder.Items) &&
                                                        individualOrder.Items.length > 0 ? (
                                                            <Box sx={{ mt: 1, pl: 2 }}>
                                                                {individualOrder.Items.map((item, idx) => (
                                                                    <Box key={item.LineItemID || idx} sx={{ mb: 0.5 }}>
                                                                        <Typography variant="body2">
                                                                            •{' '}
                                                                            <span className="fw-bold">
                                                                                {item.Quantity}x
                                                                            </span>{' '}
                                                                            {item.Title}
                                                                        </Typography>
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        ) : individualOrder.itemDetails ? (
                                                            <Box sx={{ mt: 1, pl: 2 }}>
                                                                {individualOrder.itemDetails
                                                                    .split(' - ')
                                                                    .map((item, idx) => (
                                                                        <Typography
                                                                            key={idx}
                                                                            variant="body2"
                                                                            sx={{ mb: 0.5 }}>
                                                                            • {item.trim()}
                                                                        </Typography>
                                                                    ))}
                                                            </Box>
                                                        ) : (
                                                            <Typography
                                                                variant="body2"
                                                                sx={{ mt: 1, pl: 2, fontStyle: 'italic' }}>
                                                                No items available
                                                            </Typography>
                                                        )}
                                                    </div>
                                                </Box>
                                            ))}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </div>
                        )}
                    </div>
                )}
            </ModalContainer>
        </PageContainer>
    );
};

export default PendingOrders;
